import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Form, Input, Select, Button, Checkbox, message } from 'antd';
import { navigateWithAuth } from '../utils/navigationUtils';
import styles from './SignupPage.module.scss';
import CommonHeader from './CommonHeader';
import TermsAndConditions from './TermsAndConditions';
import { auth0Config } from '../config/auth0Config';
import { userSignupTrack } from '../utils/mixpanel';

interface SignupFormData {
  email: string;
  firstName: string;
  lastName: string;
  orgName: string;
  countryCode: string;
  phoneNumber: string;
  rememberPassword?: boolean;
}

// Country codes list
const COUNTRY_CODES = [
  { code: '+1', country: 'United States' },
  { code: '+44', country: 'United Kingdom' },
  { code: '+91', country: 'India' },
  { code: '+86', country: 'China' },
  { code: '+81', country: 'Japan' },
  { code: '+33', country: 'France' },
  { code: '+49', country: 'Germany' },
  { code: '+39', country: 'Italy' },
  { code: '+34', country: 'Spain' },
  { code: '+61', country: 'Australia' },
  { code: '+64', country: 'New Zealand' },
  { code: '+27', country: 'South Africa' },
  { code: '+55', country: 'Brazil' },
  { code: '+52', country: 'Mexico' },
  { code: '+1-CA', country: 'Canada' },
  { code: '+65', country: 'Singapore' },
  { code: '+60', country: 'Malaysia' },
  { code: '+66', country: 'Thailand' },
  { code: '+62', country: 'Indonesia' },
  { code: '+63', country: 'Philippines' },
];

const countryCodeOptions = COUNTRY_CODES.map((item) => ({
  label: `${item.code} ${item.country}`,
  value: item.code,
}));

const SignupPage: React.FC = () => {
  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [termsModalOpen, setTermsModalOpen] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (values: any) => {
    const { email, firstName, lastName, orgName, countryCode, phoneNumber } = values;
    const name = [firstName, lastName].filter(s => s !== '').join(' ').trim();
    const nickname = email.split("@")[0];

    setIsLoading(true);

    try {
      // Prepare payload for the API
      const payload = {
        orgName,
        firstName,
        lastName,
        email,
        phoneNumber: `${countryCode}${phoneNumber}`,
        // subscriptions: [{ service: 'sellerportal', plan: 'basic' }],
      };


      // Call the onboard-new-org API
      const agentsellerUrl = process.env.REACT_APP_AGENTSELLER_API_URL;
      const response = await fetch(`${agentsellerUrl}/agentseller/seller-onboarding`, {
        method: 'POST',
        headers: {
          'Accept': 'application/json, text/plain, */*',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Signup failed with status ${response.status}`);
      }
      userSignupTrack({
        email: payload.email,
        name: name,
        userName: nickname,
      });

      message.success('Organization created successfully!');

      navigate('/signup-success')

    } catch (error) {
      console.error('Signup error:', error);
      const errorMessage = error instanceof Error ? error.message : 'An error occurred during signup';
      message.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={styles.signupContainer}>
      <CommonHeader />

      <main className={styles.signupMain}>
        <div className={styles.signupFormContainer}>
          <h1 className={styles.signupTitle}>Create Your Account</h1>

          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            autoComplete="off"
            className={styles.antForm}
          >
            <Form.Item
              label="Email"
              name="email"
              rules={[
                { required: true, message: 'Please enter your email' },
                { type: 'email', message: 'Please enter a valid email' },
              ]}
            >
              <Input placeholder="Enter email" size="large" />
            </Form.Item>

            <div className={styles.formRow}>
              <Form.Item
                label="First Name"
                name="firstName"
                rules={[{ required: true, message: 'Please enter your first name' }]}
                className={styles.formCol}
              >
                <Input placeholder="Enter first name" size="large" />
              </Form.Item>

              <Form.Item
                label="Last Name"
                name="lastName"
                rules={[{ required: true, message: 'Please enter your last name' }]}
                className={styles.formCol}
              >
                <Input placeholder="Enter last name" size="large" />
              </Form.Item>
            </div>

            <Form.Item
              label="Organization Name"
              name="orgName"
              rules={[{ required: true, message: 'Please enter your organization name' }]}
            >
              <Input placeholder="Enter organization name" size="large" />
            </Form.Item>

            <div className={styles.phoneRow}>
              <Form.Item
                label="Country Code"
                name="countryCode"
                rules={[{ required: true, message: 'Please select a country code' }]}
                className={styles.countryCodeCol}
              >
                <Select
                  placeholder="Select country"
                  options={countryCodeOptions}
                  size="large"
                  showSearch
                  filterOption={(input, option) =>
                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                />
              </Form.Item>

              <Form.Item
                label="Phone Number"
                name="phoneNumber"
                rules={[{ required: true, message: 'Please enter your phone number' }]}
                className={styles.phoneNumberCol}
              >
                <Input placeholder="Enter phone number" size="large" />
              </Form.Item>
            </div>
            <div className={styles.termsText}>
              <Checkbox
                checked={agreedToTerms}
                onChange={(e) => setAgreedToTerms(e.target.checked)}
              >
                By Signing Up, you agree to our{' '}
                <span
                  className={styles.termsLink}
                  onClick={(e) => { e.preventDefault(); setTermsModalOpen(true); }}
                >
                  Terms and Conditions
                </span>
              </Checkbox>
            </div>
            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                loading={isLoading}
                block
                size="large"
                className={styles.submitBtn}
                disabled={!agreedToTerms}
              >
                {isLoading ? 'Creating Account...' : 'Sign Up'}
              </Button>
            </Form.Item>

            <div className={styles.loginLink}>
              <span className={styles.loginText}>Already have an account?</span>
              <Link to="/login" className={styles.loginLinkBtn}>
                Login
              </Link>
            </div>

            <TermsAndConditions open={termsModalOpen} onClose={() => setTermsModalOpen(false)} />
          </Form>
        </div>
      </main>
    </div>
  );
};

export default SignupPage;